import { TFunction } from 'i18next';
import { OpenPlayRow } from '../types/Dashboard';

export const fmtInt = (n: number) => new Intl.NumberFormat().format(Math.round(n));
export const fmtTime = (ms: number, t:TFunction) => {
  const hours = t("stats.hours");
  const minutes = t("stats.minutes");
  const seconds = t("stats.seconds");
  if (!ms) return `0 ${minutes}`;
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h && m && ss) return `${h} ${hours} ${m} ${minutes} ${ss} ${seconds}`;
  if (h) return `${h} ${hours} ${m} ${minutes}`;
  if (m) return `${m} ${minutes} ${ss} ${seconds}`;
  return `${ss} ${seconds}`;
};
export const fmtDT = (iso: string | null) => (iso ? new Date(iso).toLocaleString('uk-UA') : '—');
export const todayYMD = () => new Date().toISOString().slice(0, 10);
export const toISODate = (d: Date) => d.toISOString().slice(0, 10);
export const msToMin = (ms: unknown) => Math.round((Number(ms ?? 0) / 60000) * 100) / 100;
export const formatMsAsClock = (ms: unknown, opts?: { withUnit?: boolean }) => {
  const totalSec = Math.max(0, Math.floor(Number(ms ?? 0) / 1000));
  const s = totalSec % 60;
  const totalMin = Math.floor(totalSec / 60);
  const m = totalMin % 60;
  const h = Math.floor(totalMin / 60);
  const two = (n: number) => String(n).padStart(2, '0');

  const core = h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
  return opts?.withUnit && h === 0 ? `${core} min` : core;
};
// Pivot long-form watch rows into recharts-ready stacked data
export function pivotWatch<T extends Record<string, any>>(
  rows: T[],
  key: 'lesson_title' | 'course_name',
) {
  const byDate = new Map<string, Record<string, number>>();
  for (const r of rows) {
    const date = r.date as string;
    const label = (r[key] ?? 'unknown') as string;
    const watchMin = (r.watch_ms as number) / 60000; // minutes for nicer axes
    const watchString = String(watchMin.toFixed(2));
    const watchFloat = Number(watchString);
    if (!byDate.has(date)) byDate.set(date, { date } as any);
    const rec = byDate.get(date)!;
    rec[label] = (rec[label] || 0) + watchFloat;
  }
  const data = Array.from(byDate.values()).sort((a, b) => (a.date > b.date ? 1 : -1));
  const keys = new Set<string>();
  data.forEach((row) => {
    Object.keys(row).forEach((k) => {
      if (k !== 'date') keys.add(k);
    });
  });
  return { data, keys: Array.from(keys) } as { data: any[]; keys: string[] };
}

// Aggregate Open vs Play rows (by chosen grouping) into daily totals
export function aggregateOpenPlay(rows: OpenPlayRow[]) {
  const map = new Map<
    string,
    { date: string; opens_sessions: number; plays_sessions: number; play_rate: number }
  >();
  for (const r of rows) {
    const rec = map.get(r.date) || {
      date: r.date,
      opens_sessions: 0,
      plays_sessions: 0,
      play_rate: 0,
    };
    rec.opens_sessions += r.opens_sessions;
    rec.plays_sessions += r.plays_sessions;
    map.set(r.date, rec);
  }
  const out = Array.from(map.values()).sort((a, b) => (a.date > b.date ? 1 : -1));
  out.forEach((r) => {
    r.play_rate = r.opens_sessions
      ? Math.round((r.plays_sessions * 1000) / r.opens_sessions) / 10
      : 0;
  });
  return out;
}

// nice distinct fills for stacked bars
export const palette = [
  '#6366f1',
  '#22c55e',
  '#f59e0b',
  '#ef4444',
  '#06b6d4',
  '#a855f7',
  '#84cc16',
  '#fb7185',
  '#14b8a6',
  '#eab308',
];
export function sumOpenPlayDaily(rows: OpenPlayRow[]) {
  const byDate = new Map<
    string,
    { date: string; opens_sessions: number; plays_sessions: number; play_rate: number }
  >();

  for (const r of rows ?? []) {
    const date = r.date;
    const opens = Number(r.opens_sessions) || 0;
    const plays = Number(r.plays_sessions) || 0;
    const acc = byDate.get(date) ?? { date, opens_sessions: 0, plays_sessions: 0, play_rate: 0 };
    acc.opens_sessions += opens;
    acc.plays_sessions += plays;
    byDate.set(date, acc);
  }

  // compute play_rate per day
  byDate.forEach((v) => {
    v.play_rate =
      v.opens_sessions > 0 ? Math.round((v.plays_sessions * 1000) / v.opens_sessions) / 10 : 0;
  });

  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}
